import { getConfig } from '@bounded-sh/core';
import { setAuthProviderInstance, setCurrentUser } from '../global';
import { setStoredAuthMethod } from './stored-auth-method';
let registrySync = null;
export function registerAuthRegistrySync(fn) {
    registrySync = fn;
}
/**
 * Atomically commit `provider` as the owner of the active session. The single await
 * happens BEFORE any state is touched; every write below it runs in one synchronous
 * block, so no listener or concurrent caller can ever observe a mixed identity
 * (e.g. the new provider with the old user, or the new user on the old signer).
 *
 * Pass `user` to publish the session's user inside the same block (listeners fire
 * after every pointer already agrees). Omit it when the caller manages the user
 * separately (e.g. a path that publishes the user only after further checks).
 * Returns the normalized user published by this commit, or null when omitted.
 */
export async function adoptSessionProvider(opts) {
    const coreConfig = await getConfig();
    coreConfig.authProvider = opts.provider;
    coreConfig.authMethod = opts.method;
    setAuthProviderInstance(opts.provider);
    registrySync === null || registrySync === void 0 ? void 0 : registrySync(opts.provider, opts.method, opts.configKey);
    setStoredAuthMethod(opts.method);
    return opts.user !== undefined ? setCurrentUser(opts.user) : null;
}
/**
 * REPAIR, not establishment: rebuild the provider pointers from the init config
 * after a prior logout nulled them, so `login()` / `logout()` can dispatch again.
 *
 * Deliberately does NOT write the stored auth-method marker, and that omission is
 * load-bearing rather than an oversight: logout()'s self-heal runs BEFORE it reads
 * `getIssuerSessionKind()`, and setStoredAuthMethod clears that kind marker for any
 * non-email method - so stamping a marker here would erase the evidence that the
 * session being logged out was hosted and silently skip the issuer-cookie bounce.
 * No session is being established here; the marker already describes the live one.
 */
export async function adoptRebuiltProvider(provider, method) {
    const coreConfig = await getConfig();
    coreConfig.authProvider = provider;
    coreConfig.authMethod = method;
    setAuthProviderInstance(provider);
    // The module registry (and its config key) is already correct here: the caller
    // rebuilt the provider through getAuthProvider(), which assigns both.
}
/**
 * The logout-side mirror: clear every pointer a session adoption sets, so a later
 * login cannot inherit a dead signer. Deliberately LEAVES the global
 * authProviderInstance in place - global.ts's login()/logout() wrappers treat a null
 * instance as "not initialized" and would throw at the user after a legitimate
 * logout; auth/index.ts's own self-heal branches rebuild the registry from
 * initConfig on the next call either way.
 */
export async function clearSessionProviderForLogout() {
    const coreConfig = await getConfig();
    coreConfig.authProvider = null;
    // 'none' is the config default meaning "no auth configured" - the closest
    // honest value for "no session owns the signer any more".
    coreConfig.authMethod = 'none';
    registrySync === null || registrySync === void 0 ? void 0 : registrySync(null, null, null);
    setStoredAuthMethod(null);
}
